import { atom } from 'nanostores'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number // milliseconds, default 3000
  createdAt: number
}

// Store for toast notifications
export const toastsStore = atom<Toast[]>([])

let toastIdCounter = 0

// Generate unique ID for toasts
function generateToastId(): string {
  return `toast-${Date.now()}-${++toastIdCounter}`
}

// Add a new toast
export function addToast(message: string, type: ToastType = 'info', duration = 4000): Toast {
  const toast: Toast = {
    id: generateToastId(),
    message,
    type,
    duration,
    createdAt: Date.now()
  }

  // Add to beginning of array (newest first)
  toastsStore.set([toast, ...toastsStore.get()])

  // Auto-remove toast after duration
  if (duration > 0) {
    setTimeout(() => {
      removeToast(toast.id)
    }, duration)
  }

  return toast
}

// Remove a toast by ID
export function removeToast(id: string): void {
  const currentToasts = toastsStore.get()
  toastsStore.set(currentToasts.filter(toast => toast.id !== id))
}

// Remove all toasts
export function clearAllToasts(): void {
  toastsStore.set([])
}

// Convenience functions for different toast types
export const toast = {
  success: (message: string, duration?: number) => addToast(message, 'success', duration),
  error: (message: string, duration?: number) => addToast(message, 'error', duration),
  warning: (message: string, duration?: number) => addToast(message, 'warning', duration),
  info: (message: string, duration?: number) => addToast(message, 'info', duration),
  remove: removeToast,
  clear: clearAllToasts
}

// Get current toasts
export function getToasts(): Toast[] {
  return toastsStore.get()
}

// Limit maximum number of visible toasts
const MAX_TOASTS = 5

// Helper to enforce toast limit
function enforceToastLimit() {
  const currentToasts = toastsStore.get()
  if (currentToasts.length > MAX_TOASTS) {
    toastsStore.set(currentToasts.slice(0, MAX_TOASTS))
  }
}

// Subscribe to changes and enforce limit
toastsStore.subscribe(() => {
  enforceToastLimit()
})